/**
 * Types and helpers for the CashDro cash-recycling terminal.
 *
 * The terminal sits on the branch's local network, so this app never talks to it directly:
 * the Electron desktop app does, and exposes it through `window.electronAPI.cashdro`
 * (see preload.js / main.js in cms2.1-desktop-app-electron). Outside Electron, or in an
 * older desktop app without the bridge, CashDro is simply unavailable.
 */

export enum CashDroOperationType {
  PAYMENT = 3,
  SALE = 4,
}

export interface CashDroConfig {
  enabled: boolean;
  ip: string;
  name: string;
  posId: string;
  hasPassword: boolean;
}

/** What the desktop app is given to reach the CashDro (synced from the branch's settings). */
export interface CashDroConfigChanges {
  enabled: boolean;
  ip: string;
  name: string;
  posId: string;
  password?: string;
}

/** Amounts are in cents, as CashDro reports them. */
export interface CashDroStatus {
  phase: "started" | "running";
  operationId: string;
  state?: "I" | "Q" | "E" | "F";
  total?: number;
  totalIn?: number;
  totalOut?: number;
}

export type CashDroOutcome = "paid" | "cancelled" | "partial";

export interface CashDroFailure {
  ok: false;
  code?: number | string;
  message: string;
  operationId?: string;
}

export type CashDroResult = { ok: true } | CashDroFailure;

export type CashDroOperationResult =
  | {
      ok: true;
      operationId: string;
      outcome: CashDroOutcome;
      total: number;
      totalIn: number;
      totalOut: number;
      changeNotAvailable: number;
    }
  | CashDroFailure;

export interface CashDroBridge {
  getConfig: () => Promise<CashDroConfig>;
  saveConfig: (config: CashDroConfigChanges) => Promise<CashDroConfig>;
  testConnection: (config: Partial<CashDroConfigChanges>) => Promise<CashDroResult>;
  startOperation: (request: {
    type: CashDroOperationType;
    amount: number;
    posUser?: string;
    aliasId?: string;
  }) => Promise<CashDroOperationResult>;
  cancelOperation: () => Promise<CashDroResult>;
  markImported: (operationId: string) => Promise<CashDroResult>;
  /** Missing in desktop app versions built before logging was added. */
  log?: (event: string, data?: unknown) => void;
  onStatus: (callback: (status: CashDroStatus) => void) => () => void;
}

export const getCashDroBridge = (): CashDroBridge | undefined =>
  window?.electronAPI?.cashdro;

/**
 * Logs a step of the CashDro flow to the DevTools console and to the desktop app's terminal,
 * so both sides of a payment can be read in one place. `data` must be plain, serialisable data.
 */
export const logCashDro = (event: string, data?: unknown) => {
  console.log(`[CashDro] ${event}`, data ?? "");
  try {
    getCashDroBridge()?.log?.(event, data);
  } catch (error) {
    console.warn("[CashDro] could not forward log to the desktop app", error);
  }
};

/** The parts of a failed request worth logging (axios errors carry the API's answer). */
export const describeRequestError = (error: any) => ({
  message: error?.message ?? String(error),
  status: error?.response?.status,
  data: error?.response?.data,
});

// CashDro limits posuser / posid / aliasid to 50 characters (manual §4.4.1).
export const toCashDroField = (value?: string) =>
  value ? value.slice(0, 50) : undefined;

const ERROR_TRANSLATION_KEYS: Record<string, string> = {
  "-1": "cashDro.errors.authenticationFailed",
  "-4": "cashDro.errors.noPermission",
  "-998": "cashDro.errors.outOfService",
  "-999": "cashDro.errors.serviceNotRunning",
  "-1900": "cashDro.errors.systemBusy",
  BUSY: "cashDro.errors.busy",
  NETWORK: "cashDro.errors.unreachable",
  CONNECTION_LOST: "cashDro.errors.connectionLost",
  NOT_CONFIGURED: "cashDro.errors.notConfigured",
};

/** Translation key for a known failure, otherwise undefined (use CashDro's own message). */
export const getCashDroErrorKey = (failure: CashDroFailure) =>
  ERROR_TRANSLATION_KEYS[String(failure.code)];
